<script setup lang="ts">
/**
 * Abstrahierte Modal-Eingabe des Members — die Skizze zur PeekMessage:
 *
 *   ┌ wer tippt: Avatar + Name + Verb („opens a ticket")
 *   └ das Modal der Kategorie: Titel, Pflichtfeld-Label, Eingabe mit
 *     Caret, Cancel/Submit
 *
 * Damit liest sich der Wert (z. B. die Order-Nummer) eindeutig als etwas,
 * das der Member selbst in ein Formular eingetragen hat.
 */
withDefaults(defineProps<{
  user?: string
  verb?: string
  /** Modal-Titel = Name der Kategorie */
  title: string
  label: string
  value: string
  required?: boolean
  submit?: string
  cancel?: string
}>(), {
  user: 'jonas.',
  verb: 'opens a ticket',
  required: true,
  submit: 'Submit',
  cancel: 'Cancel'
})
</script>

<template>
  <div class="pkm">
    <div class="pkm-who">
      <span class="pkm-avatar" aria-hidden="true">{{ user.charAt(0).toUpperCase() }}</span>
      <span class="pkm-user">{{ user }}</span>
      <span class="pkm-verb">{{ verb }}</span>
    </div>

    <div class="pkm-modal">
      <div class="pkm-head">
        <span class="pkm-title">{{ title }}</span>
        <UIcon name="i-lucide-x" class="size-[13px] pkm-close" aria-hidden="true" />
      </div>
      <span class="pkm-label">
        {{ label }}<span v-if="required" class="pkm-req">*</span>
      </span>
      <div class="pkm-input">
        <span class="pkm-value">{{ value }}</span>
        <span class="pkm-caret" aria-hidden="true" />
      </div>
      <div class="pkm-foot">
        <span class="pkm-cancel">{{ cancel }}</span>
        <span class="pkm-submit">{{ submit }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pkm {
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 0.8rem;
}

/* Wer tippt */
.pkm-who {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 1;
}

.pkm-avatar {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--nb-elevated);
  border: 1px solid var(--nb-hairline-strong);
  color: var(--nb-ink-muted);
  font-size: 0.66rem;
  font-weight: 600;
}

.pkm-user {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--nb-ink);
}

.pkm-verb {
  font-size: 0.74rem;
  color: var(--nb-ink-faint);
}

/* Das Modal */
.pkm-modal {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 10px 12px 10px;
  background: var(--nb-bg);
  border: 1px solid var(--nb-hairline);
  border-radius: var(--radius-md);
}

.pkm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 6px;
}

.pkm-title {
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--nb-ink);
}

.pkm-close {
  color: var(--nb-ink-faint);
}

.pkm-label {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nb-ink-faint);
}

.pkm-req {
  color: var(--nb-accent-soft);
  margin-left: 2px;
}

.pkm-input {
  display: flex;
  align-items: center;
  min-height: 30px;
  padding: 5px 9px;
  background: var(--nb-surface);
  border: 1px solid var(--nb-accent-border);
  border-radius: var(--radius-sm);
  font-family: var(--font-mono);
  font-size: 0.74rem;
  color: var(--nb-ink);
}

.pkm-caret {
  display: inline-block;
  width: 1px;
  height: 1.1em;
  margin-left: 2px;
  background: var(--nb-ink);
  animation: pkm-caret 1s steps(1) infinite;
}

@keyframes pkm-caret {
  50% {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pkm-caret {
    animation: none;
  }
}

.pkm-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--nb-hairline);
}

.pkm-cancel {
  font-size: 0.72rem;
  color: var(--nb-ink-faint);
}

.pkm-submit {
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--nb-accent);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
}
</style>
